import { FocusEvent, useRef, useState } from "react";
import Select, { Options, Props } from "react-select";
import { CATEGORY_SELECT_STYLES } from "src/constants/selectStyle";
import { useGravityDevice } from "src/hooks/useGravityDevice";

import {
  CategoryMenuPortal,
  DisableCondition,
  TovarTreeMenuList,
} from "./components";
import { SelectOptionTree } from "./types";
import { CLASS_PREFIX, isInsideOwnUi } from "./utils";

const componentsSelect = {
  MenuList: TovarTreeMenuList,
  MenuPortal: CategoryMenuPortal,
};

// Закрываем список при скролле страницы/фильтров, чтобы он не отрывался от
// селекта и не наезжал на шапку. Скролл внутри самого списка и внутри контрола
// (скролл выбранных чипов) меню не закрывают.
const closeMenuOnPageScroll = (e: Event) => !isInsideOwnUi(e.target);

export type SelectOptionTreeProps<isMulti extends boolean = false> = Omit<
  Props<SelectOptionTree, isMulti>,
  "options"
> &
  DisableCondition & {
    options: Options<SelectOptionTree>;
  };

export const CategorySelectTree = <isMulti extends boolean>(
  props: SelectOptionTreeProps<isMulti>,
) => {
  const { isMobile } = useGravityDevice();

  // Десктоп/планшет: меню в портал + открытие сбоку (CategoryMenuPortal).
  // Мобилка (Sheet): дефолтное меню react-select — снизу, в потоке (как раньше).
  const isPortal = !isMobile && typeof document !== "undefined";

  // menuIsOpen ведём сами: выбор категории — это клик по нативному <input>
  // чекбокса, он уводит фокус из инпута react-select. Встроенный guard react-select
  // от закрытия по blur смотрит на document.activeElement (в момент blur ещё «body»,
  // фокус в переходе) и промахивается — меню флапающе закрывается. Поэтому
  // игнорируем закрытие, если фокус ушёл в свою зону; реальные закрытия (Esc, клик
  // вне, скролл, тоггл индикатора) проходят штатно.
  const [menuOpen, setMenuOpen] = useState(false);
  const keepOpenRef = useRef(false);

  const handleMenuOpen = () => {
    keepOpenRef.current = false;
    setMenuOpen(true);
    props.onMenuOpen?.();
  };

  const handleMenuClose = () => {
    if (keepOpenRef.current) {
      keepOpenRef.current = false;
      return;
    }
    setMenuOpen(false);
    props.onMenuClose?.();
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
    // Фокус ушёл на чекбокс/чип внутри нашего UI — это не «настоящее» закрытие:
    // помечаем флаг (его прочитает handleMenuClose, который react-select вызывает
    // сразу после onBlur) и возвращаем фокус скрытому инпуту react-select.
    // preventScroll — чтобы возврат фокуса не проскроллил страницу и не закрыл
    // меню через closeMenuOnScroll.
    if (isInsideOwnUi(e.relatedTarget)) {
      keepOpenRef.current = true;
      const input = e.target;
      requestAnimationFrame(() => input.focus({ preventScroll: true }));
    }
    props.onBlur?.(e);
  };

  return (
    <Select
      classNamePrefix={CLASS_PREFIX}
      closeMenuOnSelect={false}
      // По умолчанию blurInputOnSelect=true на тач-устройствах: при выборе
      // категории инпут теряет фокус и мультиселект закрывается. Отключаем,
      // чтобы на мобилке список оставался открытым (как на десктопе).
      blurInputOnSelect={false}
      menuPlacement={isPortal ? "bottom" : undefined}
      menuPosition={isPortal ? "fixed" : undefined}
      menuPortalTarget={isPortal ? document.body : undefined}
      menuShouldScrollIntoView={isPortal ? false : undefined}
      closeMenuOnScroll={isPortal ? closeMenuOnPageScroll : undefined}
      {...props}
      menuIsOpen={menuOpen}
      onMenuOpen={handleMenuOpen}
      onMenuClose={handleMenuClose}
      onBlur={handleBlur}
      styles={CATEGORY_SELECT_STYLES}
      components={componentsSelect}
      placeholder="Выберите категорию"
    />
  );
};
